import { getI18nState, initI18n } from "./shared/i18n";
import { readTerminalConfig } from "./terminal-theme";

const toText = (value: unknown) => String(value ?? "").trim();

const DEFAULT_LANGUAGES = ["es", "en", "ca"];
const ALLOWED_LANGUAGES = new Set(["ar", "ca", "de", "en", "es", "fr", "it", "pt", "zh"]);

function normalizeLanguageCode(value: unknown): string {
  return toText(value).slice(0, 2).toLowerCase();
}

export function readRuntimeLocalizationConfig(configArg?: ReturnType<typeof readTerminalConfig> | null): {
  availableLanguages: string[];
  defaultLanguage: string;
} {
  const config = configArg && typeof configArg === "object" ? configArg : readTerminalConfig();
  const raw = config?.consumerRuntime?.localization;
  const availableLanguages = Array.from(
    new Set(
      (Array.isArray(raw?.availableLanguages) ? raw.availableLanguages : DEFAULT_LANGUAGES)
        .map((entry) => normalizeLanguageCode(entry))
        .filter((entry) => ALLOWED_LANGUAGES.has(entry))
    )
  );
  const safeLanguages = availableLanguages.length ? availableLanguages : DEFAULT_LANGUAGES;
  const requestedDefault = normalizeLanguageCode(raw?.defaultLanguage);
  const defaultLanguage = safeLanguages.includes(requestedDefault) ? requestedDefault : safeLanguages[0] || "es";
  return {
    availableLanguages: safeLanguages,
    defaultLanguage,
  };
}

export async function syncRuntimeLocalization(
  configArg?: ReturnType<typeof readTerminalConfig> | null,
  options?: { preserveStoredLanguage?: boolean }
): Promise<void> {
  const config = readRuntimeLocalizationConfig(configArg);
  const current = getI18nState();
  const sameDefault = current.defaultLanguage === config.defaultLanguage;
  const sameSupported =
    current.supportedLanguages.length === config.availableLanguages.length &&
    current.supportedLanguages.every((entry, index) => entry === config.availableLanguages[index]);

  if (sameDefault && sameSupported) return;

  await initI18n({
    supportedLanguages: config.availableLanguages,
    defaultLanguage: config.defaultLanguage,
    preserveStoredLanguage: options?.preserveStoredLanguage,
  });
}
